import type { ReactNode } from 'react';
import type { FrameColor, FrameType } from '@/types/canvas';

interface FrameWrapperProps {
  frame: FrameType;
  frameColor?: FrameColor;
  posterWidth: number;
  posterHeight: number;
  children: ReactNode;
}



const FRAME_TEXTURES: Record<
  FrameColor,
  {
    outer?: string;
    inner?: string;
    overlayOuter?: string;
    overlayInner?: string;
    colorOuter?: string;
    colorInner?: string;
  }
> = {
  oak: {
    outer: '/textures/wood-inner.webp', // Using optimized webp
    inner: '/textures/wood-inner.webp',
    overlayOuter: 'linear-gradient(0deg, rgba(185, 171, 147, 0.7), rgba(185, 171, 147, 0.7))', // Match #B9AB93
    overlayInner: 'linear-gradient(0deg, rgba(185, 171, 147, 0.6), rgba(185, 171, 147, 0.6))',
  },
  white: {
    outer: '/textures/wood-inner.webp',
    inner: '/textures/wood-inner.webp',
    overlayOuter: 'linear-gradient(0deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9))',
    overlayInner: 'linear-gradient(0deg, rgba(255,255,255,0.8), rgba(255,255,255,0.8))',
  },
  black: {
    // Pro černou můžeme použít buď barvu nebo taky texturu s tmavým overlayem
    // Pokud máme jen wood-inner, použijeme ho pro strukturu
    outer: '/textures/wood-inner.webp',
    inner: '/textures/wood-inner.webp',
    overlayOuter: 'linear-gradient(0deg, rgba(27,31,42,0.97), rgba(27,31,42,0.97))', // Téměř neprůhledná černá
    overlayInner: 'linear-gradient(0deg, rgba(17,24,39,0.97), rgba(17,24,39,0.97))',
    colorOuter: '#1b1f2a', // Fallback
    colorInner: '#111827', // Fallback
  },
  teal: {
    outer: '/textures/wood-inner.webp',
    inner: '/textures/wood-inner.webp',
    // Removed overlay as requested (raw wood texture, not turquoise)
  },
  teak: {
    outer: '/textures/wood-inner.webp',
    inner: '/textures/wood-inner.webp',
    overlayOuter: 'linear-gradient(0deg, rgba(179, 139, 109, 0.7), rgba(179, 139, 109, 0.7))', // Match #B38B6D
    overlayInner: 'linear-gradient(0deg, rgba(179, 139, 109, 0.6), rgba(179, 139, 109, 0.6))',
  },
  walnut: {
    outer: '/textures/wood-inner.webp',
    inner: '/textures/wood-inner.webp',
    overlayOuter: 'linear-gradient(0deg, rgba(78, 59, 49, 0.8), rgba(78, 59, 49, 0.8))', // Match #4E3B31
    overlayInner: 'linear-gradient(0deg, rgba(78, 59, 49, 0.7), rgba(78, 59, 49, 0.7))',
  },
  pine: {
    outer: '/textures/wood-inner.webp',
    inner: '/textures/wood-inner.webp',
    overlayOuter: 'linear-gradient(0deg, rgba(230, 219, 198, 0.5), rgba(230, 219, 198, 0.5))', // Match #E6DBC6
    overlayInner: 'linear-gradient(0deg, rgba(230, 219, 198, 0.4), rgba(230, 219, 198, 0.4))',
  },
};

const buildTextureBackground = (
  texture: {
    outer?: string;
    inner?: string;
    overlayOuter?: string;
    overlayInner?: string;
    colorOuter?: string;
    colorInner?: string;
  },
  layer: 'outer' | 'inner',
) => {
  const base = layer === 'outer' ? texture.outer : texture.inner;
  const color = layer === 'outer' ? texture.colorOuter : texture.colorInner;
  const overlay = layer === 'outer' ? texture.overlayOuter : texture.overlayInner;

  if (color) return color;

  if (base) {
    // Optimalizace: Pokud je to jen barva/gradient a není URL, vrátíme to přímo
    return overlay ? `${overlay}, url(${base})` : `url(${base})`;
  }

  return '#111827';
};

// Helper funkce pro rozpoznání, jestli je hodnota barva nebo obrázek/gradient
const isColorValue = (value: string): boolean => {
  return value.startsWith('#') || value.startsWith('rgb') || value.startsWith('rgba') ||
    (value.length <= 20 && !value.includes('url(') && !value.includes('gradient'));
};

import { memo } from 'react';

// ... helper functions ...

export const FrameWrapper = memo(function FrameWrapper({ frame, frameColor = 'oak', posterWidth, posterHeight, children }: FrameWrapperProps) {
  if (frame === 'none') {
    return (
      <div
        className="relative"
        style={{
          width: posterWidth,
          height: posterHeight,
          background: '#ffffff',
          boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
          border: '1px solid rgba(15,23,42,0.05)',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    );
  }

  if (frame === 'hanger') {
    const barHeight = Math.max(6, Math.round(posterHeight * 0.02));
    const strapHeight = Math.max(6, Math.round(barHeight * 0.6));
    const texture = FRAME_TEXTURES[frameColor];

    return (
      <div className="relative flex flex-col items-center" style={{ width: posterWidth }}>
        <div
          className="relative w-full"
          style={{
            height: barHeight,
            ...(() => {
              const bgValue = buildTextureBackground(texture, 'outer');
              return isColorValue(bgValue)
                ? { backgroundColor: bgValue }
                : {
                  backgroundImage: bgValue,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                };
            })(),
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          }}
        >
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              top: -strapHeight + 2,
              width: 3,
              height: strapHeight,
              background: 'linear-gradient(180deg, rgba(148,163,184,0.85) 0%, rgba(209,213,219,0.65) 100%)',
              borderRadius: 999,
              boxShadow: '0 0 2px rgba(0,0,0,0.18)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow: 'inset 0 2px 2px rgba(255,255,255,0.45), inset 0 -3px 4px rgba(0,0,0,0.2)',
            }}
          />
        </div>

        <div
          className="relative w-full"
          style={{
            width: posterWidth,
            height: posterHeight,
            backgroundColor: '#ffffff',
            boxShadow: '0 10px 24px rgba(15,23,42,0.18)',
            border: '1px solid rgba(15,23,42,0.08)',
          }}
        >
          {children}
        </div>

        <div
          className="relative w-full"
          style={{
            height: barHeight,
            ...(() => {
              const bgValue = buildTextureBackground(texture, 'outer');
              return isColorValue(bgValue)
                ? { backgroundColor: bgValue }
                : {
                  backgroundImage: bgValue,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                };
            })(),
            boxShadow: '0 -3px 6px rgba(15,23,42,0.18)',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow: 'inset 0 3px 4px rgba(0,0,0,0.2), inset 0 -2px 2px rgba(255,255,255,0.45)',
            }}
          />
        </div>
      </div>
    );
  }

  // Frame - zatím zobrazujeme jen plakát bez rámečku (verze není stabilní, dokončíme později)
  if (frame === 'frame') {
    return (
      <div
        className="relative"
        style={{
          width: posterWidth,
          height: posterHeight,
          background: '#ffffff',
          boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
          border: '1px solid rgba(15,23,42,0.05)',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    );
  }

  // Frame dimensions according to Figma design
  const frameWidth = 20;

  // Total dimensions including frame
  const totalWidth = posterWidth + 2 * frameWidth;
  const totalHeight = posterHeight + 2 * frameWidth;

  const texture = FRAME_TEXTURES[frameColor];

  // OPTIMIZATION: Use tiling with smaller textures instead of creating heavy large bitmaps
  // Using 256px or 512px background size is much more memory efficient than 'cover' on large canvases
  const frameBackgroundStyle = (() => {
    const bgValue = buildTextureBackground(texture, 'outer');
    return isColorValue(bgValue)
      ? { backgroundColor: bgValue }
      : {
        backgroundImage: bgValue,
        backgroundSize: '256px 256px', // Tile the texture
        backgroundRepeat: 'repeat',
      };
  })();

  return (
    <div
      className="relative bg-white"
      style={{
        width: totalWidth,
        height: totalHeight,
        boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.25)',
      }}
    >
      {/* Left frame - vertical */}
      <div
        className="absolute left-0 top-0 h-full"
        style={{
          width: frameWidth,
          ...frameBackgroundStyle,
        }}
      />

      {/* Right frame - rotated 180deg background via transform or just same texture */}
      <div
        className="absolute right-0 top-0 h-full"
        style={{
          width: frameWidth,
          ...frameBackgroundStyle, // Textures are usually generic enough to not need rotation, or use CSS transform if directional
          transform: 'scaleX(-1)', // Flip texture horizontally for variation
        }}
      />

      {/* Top frame - horizontal */}
      <div
        className="absolute left-0 top-0 w-full"
        style={{
          height: frameWidth,
          ...frameBackgroundStyle,
        }}
      />

      {/* Bottom frame - rotated 180deg */}
      <div
        className="absolute left-0 bottom-0 w-full"
        style={{
          height: frameWidth,
          ...frameBackgroundStyle,
          transform: 'scaleY(-1)', // Flip texture vertically
        }}
      />

      {/* Corners - Optional simple overlays to hide seams if needed, or CSS gradients */}
      {/* For simplicity and performance we skip complex corner stitching logic here as wood content is forgiving */}

      {/* Poster content with inner shadow - centered in frame */}
      <div
        className="absolute"
        style={{
          left: frameWidth,
          top: frameWidth,
          width: posterWidth,
          height: posterHeight,
          background: '#ffffff',
          overflow: 'hidden',
          boxShadow: 'inset 0px 4px 4px 12px rgba(0,0,0,0.25)',
        }}
      >
        {children}
      </div>
    </div>
  );
});
